import {ProviderType} from "@/app/api/integrations/connect/types";
import type {ProjectsArchiveFilter} from "@/app/util/projectsUtil";

export function posthogApiKeyAPI() {
  return `/api/v1/posthog` as const;
}

export function featureFlagsAPI() {
  return `/api/v1/config/flags` as const;
}

export function companiesAPI() {
  return `/api/v1/company` as const;
}

export function plgCompaniesAPI() {
  return `${companiesAPI()}/plg` as const;
}

export function plgOnboardingDefaultsAPI() {
  return `${plgCompaniesAPI()}/defaults` as const;
}

export function plgDomainJoinRequestsAPI() {
  return `${plgCompaniesAPI()}/domain-join-requests` as const;
}

export function companyPlgDomainJoinRequestAPI<C extends number | string, R extends string>(companyId: C, requestId: R) {
  return `${companyUsersAPI(companyId)}/domain-join-requests/${encodeURIComponent(requestId)}` as const;
}

export function companyAPI<C extends number | string>(companyId: C) {
  return `${companiesAPI()}/${companyId}` as const;
}

export function companyTrialAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/billing/trial` as const;
}

export function ticketBatchesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/ticket-batches` as const;
}

export function companyResearchReportsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/research-reports` as const;
}

export function companySettingsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/settings` as const;
}

export function companyRoadmapDefaultAPI<C extends number | string>(companyId: C) {
  return `${companySettingsAPI(companyId)}/roadmap-default` as const;
}

export function projectsViewSettingsAPI<C extends number | string>(companyId: C, viewId: string) {
  return `${companyAPI(companyId)}/view-settings/${viewId}` as const;
}

export function companyUsersAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/users` as const;
}

export function projectsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/projects` as const;
}

export function projectsListAPI<C extends number | string>(
  companyId: C,
  archiveFilter: ProjectsArchiveFilter = "active",
) {
  const base = projectsAPI(companyId);
  if (archiveFilter !== "active") return `${base}?archiveFilter=${archiveFilter}` as const;
  return base;
}

export function projectConvertSpecAPI<C extends number | string>(companyId: C) {
  return `${projectsAPI(companyId)}/convertSpec` as const;
}

export function projectAPI<T extends string, C extends number | string>(companyId: C, projectId: T) {
  return `${projectsAPI(companyId)}/${projectId}` as const;
}

export function projectArchiveStatusAPI<T extends string, C extends number | string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/archive-status` as const;
}

export function projectPhaseDatesAPI<T extends string, C extends number | string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/phase-dates` as const;
}

export function projectDescriptionAPI<T extends string, C extends number | string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/description` as const;
}

export function quickWinsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/quick-wins` as const;
}

export function quickWinAPI<T extends string, C extends number | string>(companyId: C, quickWinId: T) {
  return `${quickWinsAPI(companyId)}/${quickWinId}` as const;
}

export function bugsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/bugs` as const;
}

export function bugAPI<T extends string, C extends number | string>(companyId: C, bugId: T) {
  return `${bugsAPI(companyId)}/${bugId}` as const;
}

export function groupsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/groups` as const;
}

export function groupAPI<C extends number | string, G extends string>(companyId: C, groupId: G) {
  return `${groupsAPI(companyId)}/${groupId}` as const;
}

export function ideaPromoteAPI<C extends number | string, T extends string>(companyId: C, ideaId: T) {
  return `${companyAPI(companyId)}/ideas/${ideaId}/promote` as const;
}

export function tagsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/tags` as const;
}

export function releasesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/releases` as const;
}

export function companySecretsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/secrets` as const;
}

export function companyCodeupAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/codeup` as const;
}

export function integrationCreditRewardsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration-credit-rewards` as const;
}

export function plgRewardAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/plg-rewards` as const;
}

export function workspaceMemoriesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/memories` as const;
}

export function workspaceMemoriesListAPI<C extends number | string>(companyId: C) {
  return `${workspaceMemoriesAPI(companyId)}/list` as const;
}

export function workspaceMemoryAPI<C extends number | string, M extends string>(companyId: C, memoryId: M) {
  return `${companyAPI(companyId)}/memories/${memoryId}` as const;
}

export function workspaceMemoryArchiveAPI<C extends number | string, M extends string>(companyId: C, memoryId: M) {
  return `${workspaceMemoryAPI(companyId, memoryId)}/archive` as const;
}

export function workspaceMemoryRestoreAPI<C extends number | string, M extends string>(companyId: C, memoryId: M) {
  return `${workspaceMemoryAPI(companyId, memoryId)}/restore` as const;
}

export function workspaceMemoryApproveAPI<C extends number | string, M extends string>(companyId: C, memoryId: M) {
  return `${workspaceMemoryAPI(companyId, memoryId)}/approve` as const;
}

export function workspaceMemoryRejectAPI<C extends number | string, M extends string>(companyId: C, memoryId: M) {
  return `${workspaceMemoryAPI(companyId, memoryId)}/reject` as const;
}

export function teamsAPI<C extends number | string>(companyId: C, scope?: "mine") {
  const path = `${companyAPI(companyId)}/teams` as const;
  return scope ? `${path}?scope=${scope}` as const : path;
}

export function teamAPI<C extends number | string, T extends string>(companyId: C, teamId: T) {
  return `${teamsAPI(companyId)}/${teamId}` as const;
}

export function teamMembersAPI<C extends number | string, T extends string>(companyId: C, teamId: T) {
  return `${teamAPI(companyId, teamId)}/members` as const;
}

export function teamMembershipsAPI<C extends number | string>(companyId: C, scope?: "mine") {
  const path = `${teamsAPI(companyId)}/members` as const;
  return scope ? `${path}?scope=${scope}` as const : path;
}

export function goalsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/goals` as const;
}

export function goalAPI<C extends number | string, G extends string>(companyId: C, goalId: G) {
  return `${goalsAPI(companyId)}/${goalId}` as const;
}

export function goalArchiveAPI<C extends number | string, G extends string>(companyId: C, goalId: G) {
  return `${goalAPI(companyId, goalId)}/archive` as const;
}

export function goalStatusSummaryAPI<C extends number | string, G extends string>(companyId: C, goalId: G) {
  return `${goalAPI(companyId, goalId)}/status/summary` as const;
}

export function goalStatusUpdatesAPI<C extends number | string, G extends string>(companyId: C, goalId: G) {
  return `${goalAPI(companyId, goalId)}/status/updates` as const;
}

export function goalStatusSummariesBatchAPI<C extends number | string>(companyId: C) {
  return `${goalsAPI(companyId)}/status/summaries` as const;
}

export function companyRepoTestSetupAPI<C extends number | string>(companyId: C) {
  return `${companyCodeupAPI(companyId)}/setup/test` as const;
}

export function gitProviderTokenAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/git-provider-token` as const;
}

export function repositorySelectionAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/repositories/select` as const;
}

export function gitLabRotateTokenAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/gitlab/rotate-token` as const;
}

export function companySetupRunsAPI<C extends number | string>(companyId: C) {
  return `${companyCodeupAPI(companyId)}/setup/runs` as const;
}

export function repoSummariesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/repo-summaries` as const;
}

export function repoResearchAPI<C extends number | string>(companyId: C) {
  return `${repoSummariesAPI(companyId)}/research` as const;
}

export function repoGuidanceAPI<C extends number | string>(companyId: C) {
  return `${repoSummariesAPI(companyId)}/guidance` as const;
}

export function catalogFeaturesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/catalog` as const;
}

export function catalogFeatureAPI<C extends number | string, F extends string>(companyId: C, featureId: F) {
  return `${catalogFeaturesAPI(companyId)}/feature/${featureId}` as const;
}

export function catalogFeatureUpdatesAPI<C extends number | string, F extends string>(companyId: C, featureId: F) {
  return `${catalogFeatureAPI(companyId, featureId)}/updates` as const;
}

export function catalogSectionAPI<C extends number | string>(companyId: C) {
  return `${catalogFeaturesAPI(companyId)}/section` as const;
}

export function catalogSectionRenameAPI<C extends number | string>(companyId: C, sectionIndex: number) {
  return `${catalogSectionAPI(companyId)}/${sectionIndex}` as const;
}

export function catalogMoveFeaturesAPI<C extends number | string>(companyId: C) {
  return `${catalogFeaturesAPI(companyId)}/move-features` as const;
}

export function catalogCheckAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/catalog/check` as const;
}

export function catalogueBootstrapRetryAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/catalog/bootstrap/retry` as const;
}

export function catalogFeedbackAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/catalog/feedback` as const;
}

export function catalogFeedbackSubmitAPI<C extends number | string>(companyId: C) {
  return `${catalogFeedbackAPI(companyId)}/submit` as const;
}

export function adminCatalogueBootstrapAPI() {
  return `${adminAPI()}/jobs/catalogue_bootstrap` as const;
}

export function adminCatalogueUpdateAPI() {
  return `${adminAPI()}/jobs/catalogue_update` as const;
}

export function adminPrototypeWalkthroughAPI() {
  return `${adminAPI()}/jobs/prototype_walkthrough` as const;
}

export function adminSignalsProcessingAPI() {
  return `${adminAPI()}/jobs/signals_processing` as const;
}

export function adminBackfillChangesGraphAPI() {
  return `${adminAPI()}/jobs/backfill_changes_graph` as const;
}

export function adminCleanupProjectRiskGraphNodesAPI() {
  return `${adminAPI()}/jobs/cleanup_project_risk_graph_nodes` as const;
}

export function adminBackfillEmbeddingSourceTimestampsAPI() {
  return `${adminAPI()}/jobs/backfill_embedding_source_timestamps` as const;
}

export function adminBackfillEmbeddingSearchVectorsAPI() {
  return `${adminAPI()}/jobs/backfill_embedding_search_vectors` as const;
}

export function adminBackfillChangesAPI() {
  return `${adminAPI()}/jobs/backfill_changes` as const;
}

export function adminBackfillSignalsAPI() {
  return `${adminAPI()}/jobs/backfill_signals` as const;
}

export function adminInsightCreationAPI() {
  return `${adminAPI()}/jobs/insight_creation` as const;
}

export function adminInferredProjectDiscoveryAPI() {
  return `${adminAPI()}/jobs/inferred_project_discovery` as const;
}

export function adminInferredProjectDiscoveryBackfillAPI() {
  return `${adminAPI()}/jobs/inferred_project_discovery_backfill` as const;
}

export function adminMemoryExtractionAPI() {
  return `${adminAPI()}/jobs/memory_extraction` as const;
}

export function adminProjectsInsightsAnalysisAPI() {
  return `${adminAPI()}/jobs/projects_insights_analysis` as const;
}

export function adminProjectUpdatesAPI() {
  return `${adminAPI()}/jobs/project_updates` as const;
}

export function adminGoalProcessingAPI() {
  return `${adminAPI()}/jobs/goal_processing` as const;
}

export function adminHomeReportGenerationAPI(cadence: "daily" | "weekly" = "daily") {
  return `${adminAPI()}/jobs/home_report_generation?cadence=${cadence}` as const;
}

export function adminChangesReportGenerationAPI() {
  return `${adminAPI()}/jobs/changes_report_generation` as const;
}

export function adminChangesReportBackfillAPI() {
  return `${adminAPI()}/jobs/changes_report_backfill` as const;
}

export function adminHomeReportsAPI() {
  return `${adminAPI()}/home-reports` as const;
}

export function adminHomeReportDeliveryAPI() {
  return `${adminHomeReportsAPI()}/enqueue-delivery` as const;
}

export function adminLlmUsageAPI() {
  return `${adminAPI()}/llm-usage` as const;
}

export function prototypeWalkthroughAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/prototype-walkthrough` as const;
}

export function websiteAnalysisAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/website-analysis` as const;
}

export function websiteRescanAPI<C extends number | string>(companyId: C) {
  return `${websiteAnalysisAPI(companyId)}/rescan` as const;
}

export function repoMetasAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/repo-metadata` as const;
}

export function repoMetaAPI<C extends number | string, R extends string>(companyId: C, repoId: R) {
  return `${repoMetasAPI(companyId)}/${repoId}` as const;
}

export function repoRescanAPI<C extends number | string, R extends string>(companyId: C, repoId: R) {
  return `${repoMetaAPI(companyId, repoId)}/rescan` as const;
}

export function repoCommentsAPI<C extends number | string, R extends string>(companyId: C, repoId: R) {
  return `${repoMetaAPI(companyId, repoId)}/comments` as const;
}

export function repoCommentAPI<C extends number | string, R extends string, I extends string>(
  companyId: C, repoId: R, commentId: I
) {
  return `${repoCommentsAPI(companyId, repoId)}/${commentId}` as const;
}

/**
 * Upload guidance for repository scan (comments + instructions).
 */
export function repoGuidanceUploadAPI<C extends number | string, R extends string>(companyId: C, repoId: R) {
  return `${repoMetaAPI(companyId, repoId)}/guidance` as const;
}

/**
 * Upsert raw guidance content for repository (uses PUT method).
 */
export function repoGuidanceUpsertAPI<C extends number | string, R extends string>(companyId: C, repoId: R) {
  return `${repoMetaAPI(companyId, repoId)}/guidance` as const;
}

export function jobsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/jobs` as const;
}

export function jobsFetchAPI<C extends number | string>(companyId: C) {
  return `${jobsAPI(companyId)}/fetch` as const;
}

export function jobAPI<C extends number | string, J extends string>(companyId: C, jobId: J) {
  return `${jobsAPI(companyId)}/${jobId}` as const;
}

export function jobRunsAPI<C extends number | string, J extends string>(companyId: C, jobId: J) {
  return `${jobAPI(companyId, jobId)}/runs` as const;
}

export function jobRunAPI<C extends number | string, R extends string>(companyId: C, runId: R) {
  return `${jobsAPI(companyId)}/runs/${runId}` as const;
}

export function taskRunsAPI<C extends number | string, T extends string>(companyId: C, taskId: T) {
  return `${companyAPI(companyId)}/tasks/${taskId}/runs` as const;
}

export function artifactsListAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/artifacts/list` as const;
}

export function artifactsSingleListAPI<C extends number | string, T extends string>(companyId: C, id: T) {
  return `${artifactsListAPI(companyId)}/${id}` as const;
}

export function integrationsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integrations` as const;
}

export function signLinkAPI<C extends number | string>(companyId: C) {
  return `${integrationsAPI(companyId)}/signed` as const;
}

export function integrationAPI<C extends number | string, T extends ProviderType>(companyId: C, type: T) {
  return `${companyAPI(companyId)}/integration/${type}` as const;
}

export function integrationRefreshAPI<C extends number | string, T extends ProviderType>(companyId: C, type: T) {
  return `${integrationAPI(companyId, type)}/refresh` as const;
}

export function managedConnectionsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/managed-connections` as const;
}

export function managedConnectionToolsAPI<C extends number | string, S extends string>(companyId: C, slug: S) {
  return `${managedConnectionsAPI(companyId)}/${encodeURIComponent(slug)}/tools` as const;
}

export function customMCPConnectionsAPI<C extends number | string>(companyId: C) {
  return `${managedConnectionsAPI(companyId)}/custom` as const;
}

export function customMCPFaviconAPI<C extends number | string>(companyId: C, endpointUrl: string) {
  return `${customMCPConnectionsAPI(companyId)}/favicon?endpointUrl=${encodeURIComponent(endpointUrl)}` as const;
}

export function customMCPOAuthCallbackAPI() {
  return "/api/v1/integrations/custom-mcp/oauth/callback" as const;
}

export function githubManageUrlAPI<C extends number | string>(companyId: C) {
  return `${integrationAPI(companyId, "github")}/manage-url` as const;
}

export function githubPersonalOAuthStatusAPI<C extends number | string>(companyId: C) {
  return `${integrationAPI(companyId, "github")}/personal/status` as const;
}

export function jiraForgeAppStatusAPI<C extends number | string>(companyId: C) {
  return `${integrationAPI(companyId, "jira")}/forge-app-status` as const;
}

export function googleDriveFoldersAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/google-drive/folders` as const;
}

export function googleDriveAddFolderAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/google-drive/add-folder` as const;
}

export function googleDriveRemoveFolderAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/google-drive/remove-folder` as const;
}

export function googleDriveAttachItemsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/google-drive/attach-items` as const;
}

export function googleDriveRemoveFileAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/google-drive/remove-file` as const;
}

export function contextConfigurationAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/context-configuration` as const;
}

export function slackChannelsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/slack/channels` as const;
}

export function teamsChannelsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/teams/channels` as const;
}

export function zoomSyncModeAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/zoom/sync-mode` as const;
}

export function teamsMeetingNotesAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/teams/meeting-notes` as const;
}

export function teamsMeetingInstallsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/teams/meeting-installs` as const;
}

export function notionRemoveRootAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/integration/notion/remove-root` as const;
}

export function jiraRequiredFieldsAPI<C extends number | string>(companyId: C) {
  return `${integrationsAPI(companyId)}/jira/required-fields` as const;
}

export function linkPreviewAPI<C extends number | string>(companyId: C) {
  return `${integrationsAPI(companyId)}/link-preview` as const;
}

export function projectDocumentsAPI<T extends string, C extends number | string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/docs` as const;
}

export function projectStatusSummaryAPI<C extends number | string, T extends string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/status/summary` as const;
}

export function projectAcceptanceCriterionStatusAPI<C extends number | string, T extends string, U extends string>(
  companyId: C,
  projectId: T,
  criterionId: U,
) {
  return `${projectAPI(companyId, projectId)}/status/acceptance-criteria/${criterionId}` as const;
}

export function projectRiskStatusAPI<C extends number | string, T extends string, U extends string>(
  companyId: C,
  projectId: T,
  riskId: U,
) {
  return `${projectAPI(companyId, projectId)}/status/risks/${riskId}` as const;
}

export function projectStatusSummariesBatchAPI<C extends number | string>(companyId: C) {
  return `${projectsAPI(companyId)}/status/summaries` as const;
}

export function projectSummaryActivitiesBatchAPI<C extends number | string>(companyId: C) {
  return `${projectsAPI(companyId)}/status/activities` as const;
}

export function projectAnalysisStateBatchAPI<C extends number | string>(companyId: C) {
  return `${projectsAPI(companyId)}/status/analysis-state` as const;
}

export function projectStatusUpdatesAPI<C extends number | string, T extends string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/status/updates` as const;
}

export function projectSignalsFeedAPI<C extends number | string, T extends string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/summary/signals` as const;
}

export function projectActivityFetchAPI<C extends number | string, T extends string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/activity/fetch` as const;
}

export function projectActivityRestoreAPI<C extends number | string, T extends string>(companyId: C, projectId: T) {
  return `${projectAPI(companyId, projectId)}/activity/restore` as const;
}

export function companyActivityFetchAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/activity/fetch` as const;
}

export function childDocumentsAPI<T extends string, C extends number | string>(companyId: C, parentId: T) {
  return `${companyAPI(companyId)}/documents/byParent/${parentId}` as const;
}

export function documentAPI<T extends string, C extends number | string>(companyId: C, documentId: T) {
  return `${companyAPI(companyId)}/document/${documentId}` as const;
}

export function createDocumentAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/document` as const;
}

export function userAPI() {
  return '/api/v1/user' as const;
}

export function magicLinkRequestAPI() {
  return '/api/v1/auth/magic-link' as const;
}

export function recaptchaVerificationAPI() {
  return '/api/v1/auth/recaptcha' as const;
}

export function earlyAccessRequestAPI() {
  return `${userAPI()}/early-access-request` as const;
}

export function companyUserAPI<T extends string, C extends number | string>(companyId: C, userId: T) {
  return `${companyAPI(companyId)}/user/${userId}` as const;
}

export function companyUsersInvitesAPI<C extends number | string>(companyId: C) {
  return `${companyUsersAPI(companyId)}/invites` as const;
}



export function companyPulseStatusAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/pulse/status` as const;
}

export function companyPulseDigestsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/pulse/digests` as const;
}

export function companyPulseFeedAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/pulse/feed` as const;
}

export function companySignalsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/signals` as const;
}

export function companyDiscussionTopicsByIdsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/discussion-topics/by-ids` as const;
}

export function companyCallNoteByProviderRefAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/call-notes/by-provider-ref` as const;
}

export function companyEvidenceHydrationAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/evidence/hydrate` as const;
}

export function companyInsightsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/insights` as const;
}

export function companyInsightUpdatesAPI<C extends number | string>(companyId: C, insightId: string) {
  return `${companyInsightsAPI(companyId)}/${encodeURIComponent(insightId)}/updates` as const;
}

export function companyInsightArchiveAPI<C extends number | string>(companyId: C, insightId: string) {
  return `${companyInsightsAPI(companyId)}/${encodeURIComponent(insightId)}/archive` as const;
}

export function companyInsightRoleCatalogAPI<C extends number | string>(companyId: C) {
  return `${companyInsightsAPI(companyId)}/role-catalog` as const;
}

export function companyChangesReportsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/changes-reports` as const;
}

export function companyTrendsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/trends` as const;
}

export function historyHeatmapAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/history/heatmap` as const;
}

export function historyHeatmapDetailsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/history/heatmap/details` as const;
}

export function userUsageAPI() {
  return `${userAPI()}/usage` as const;
}

export function userUsageLimitsAPI() {
  return `${userUsageAPI()}/limits` as const;
}

export function userCompaniesInvitesAPI() {
  return `${userAPI()}/companies/invites` as const;
}

export function userCompanyAPI<C extends number | string>(companyId: C) {
  return `${userAPI()}/company/${companyId}` as const;
}

export function publicGlobalConfigAPI() {
  return `${userAPI()}/config` as const;
}

export function userPreferencesAPI() {
  return `${userAPI()}/preferences` as const;
}

export function userInfoAPI() {
  return `${userAPI()}/info` as const;
}

export function userPlansAPI() {
  return `${userAPI()}/plans` as const;
}

export function userSubscriptionAPI() {
  return `${userAPI()}/billing/subscription` as const;
}

export function enterpriseSubscriptionAPI() {
  return `${userAPI()}/billing/subscription/enterprise` as const;
}

export function commentsThreadAPI<T extends string, C extends number | string>(companyId: C, threadId: T) {
  return `${companyAPI(companyId)}/comments/thread/${threadId}` as const;
}

export function commentsThreadsByDocAPI<T extends string, C extends number | string>(companyId: C, documentId: T) {
  return `${companyAPI(companyId)}/comments/byDocument/${documentId}` as const;
}

export function commentAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/comment` as const;
}

export function promptsDownloadAPI<T extends number | string, P extends string>(companyId: T, projectId: P) {
  return `${projectAPI(companyId, projectId)}/prompts/download` as const;
}

export function planningAPI<T extends number | string>(companyId: T) {
  return `${companyAPI(companyId)}/planning` as const;
}

export function planningTargetAPI<T extends number | string>(companyId: T) {
  return `${planningAPI(companyId)}/target` as const;
}

export function companyAttachmentsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/attachments` as const;
}

export function attachmentPresignAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/attachments/presign` as const;
}

export function imageAttachmentPresignAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/attachments/images/presign` as const;
}

export function projectAttachmentsAPI<C extends number | string>(companyId: C, projectId: string) {
  return `${projectAPI(companyId, projectId)}/attachments` as const;
}

export function attachmentAPI<C extends number | string>(companyId: C, attachmentId: string) {
  return `${companyAPI(companyId)}/attachments/${attachmentId}` as const;
}

export function attachmentDownloadUrlAPI<C extends number | string>(companyId: C, attachmentId: string) {
  return `${attachmentAPI(companyId, attachmentId)}/download-url` as const;
}

export function attachmentProcessAPI<C extends number | string>(companyId: C, attachmentId: string) {
  return `${attachmentAPI(companyId, attachmentId)}/process` as const;
}

export function jobsStartAPI<T extends number | string>(companyId: T) {
  return `${companyAPI(companyId)}/dev/jobs/start` as const;
}

// Admin APIs

export function adminAPI() {
  return "/api/v1/admin" as const;
}

export function adminTiersAPI() {
  return `${adminAPI()}/tiers` as const;
}

export function adminTierAPI<T extends string>(tierId: T) {
  return `${adminTiersAPI()}/${tierId}` as const;
}

export function adminConfigAPI() {
  return `${adminAPI()}/config` as const;
}

export function adminCreditDeductionMultipliersAPI() {
  return `${adminAPI()}/credit-deduction-multipliers` as const;
}

export function adminCompanySpecsEnabledAPI<C extends number | string>(companyId: C) {
  return `${adminAPI()}/companies/${companyId}/specs-enabled` as const;
}

export function adminAgentsMemSnapshotAPI() {
  return `${adminAPI()}/debug/agents-mem-snapshot` as const;
}

export function adminRunRepoAnalysisAPI() {
  return `${adminAPI()}/jobs/repo_analysis` as const;
}

export function adminOrgResearchMetaAPI() {
  return `${adminAPI()}/jobs/org_research_meta` as const;
}

export function adminUsersAPI() {
  return `${adminAPI()}/users` as const;
}

export function adminUserGlobalRoleAPI<U extends string>(userId: U) {
  return `${adminUsersAPI()}/${userId}/global-role` as const;
}

export function adminUserCompanyRoleAPI<U extends string>(userId: U) {
  return `${adminUsersAPI()}/${userId}/company-role` as const;
}

export function adminUsersRolesAPI() {
  return `${adminUsersAPI()}/roles` as const;
}

export function adminCompaniesAPI() {
  return `${adminAPI()}/companies` as const;
}

export function adminOrganizationsAPI() {
  return `${adminAPI()}/organizations` as const;
}

export function adminOnboardingAPI(params?: {cursor?: string; limit?: number}) {
  const query = new URLSearchParams();
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.limit) query.set("limit", String(params.limit));
  return `${adminAPI()}/onboarding${query.size ? `?${query}` : ""}` as const;
}

export function adminOnboardingSlackChannelsAPI() {
  return `${adminAPI()}/onboarding/slack-channels` as const;
}

export function adminOrganizationCreditPacksAPI<O extends string>(organizationId: O) {
  return `${adminOrganizationsAPI()}/${organizationId}/credit-packs` as const;
}

export function adminOrganizationCreditPackArchiveAPI<O extends string, P extends string>(organizationId: O, packId: P) {
  return `${adminOrganizationCreditPacksAPI(organizationId)}/${packId}/archive` as const;
}

export function adminHomeReportEmailPreviewAPI() {
  return `${adminAPI()}/notifications/home-report-email/preview` as const;
}

export function adminMarkdownEmailPreviewAPI() {
  return `${adminAPI()}/notifications/markdown-email-charts/preview` as const;
}

export function adminHomeReportEmailSendAPI() {
  return `${adminAPI()}/notifications/home-report-email/send` as const;
}

export function adminHomeReportSlackPreviewAPI() {
  return `${adminAPI()}/notifications/home-report-slack/preview` as const;
}

export function adminHomeReportSlackSendAPI() {
  return `${adminAPI()}/notifications/home-report-slack/send` as const;
}

// Work Log APIs

export function workLogAPI<C extends string | number>(companyId: C) {
  return `${companyAPI(companyId)}/worklog` as const;
}

export function workLogProjectEntriesAPI<C extends string | number, P extends string>(companyId: C, projectId: P) {
  return `${workLogAPI(companyId)}/project/${projectId}` as const;
}

// Pending Jobs API
export function pendingJobsAPI<C extends string | number>(companyId: C) {
  return `${companyAPI(companyId)}/pending-jobs` as const;
}

// Quick-Win Init API
export function quickWinInitAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/quick-win-init` as const;
}

export function quickWinInitStatusAPI<C extends number | string, J extends string>(companyId: C, pendingJobId: J) {
  return `${quickWinInitAPI(companyId)}/${pendingJobId}/status` as const;
}

// Bug Init API
export function bugInitAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/bug-init` as const;
}

export function bugInitStatusAPI<C extends number | string, J extends string>(companyId: C, pendingJobId: J) {
  return `${bugInitAPI(companyId)}/${pendingJobId}/status` as const;
}

// Workspace API Keys
export function companyApiKeysAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/api-keys` as const;
}

// MCP API
export function mcpAPI() {
  return `/api/v1/mcp` as const;
}

// Runs API
export function runsAPI<C extends string | number>(companyId: C) {
  return `${companyAPI(companyId)}/runs` as const;
}

// Workspace Chats API
export function chatsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/chats` as const;
}

export function chatAPI<C extends number | string, I extends string>(companyId: C, chatId: I) {
  return `${chatsAPI(companyId)}/${chatId}` as const;
}

export function workspaceChatActionPromptAPI<C extends number | string>(companyId: C, actionId: string) {
  return `${chatsAPI(companyId)}/actions/${encodeURIComponent(actionId)}/prompt` as const;
}

export function chatGenerateTitleAPI<C extends number | string, I extends string>(companyId: C, chatId: I) {
  return `${chatAPI(companyId, chatId)}/generate-title` as const;
}

export function knowledgeSearchAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/knowledge-search` as const;
}

// Dashboards API
export function dashboardsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/dashboards` as const;
}

export function dashboardAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardsAPI(companyId)}/${dashboardId}` as const;
}

export function dashboardRegenerateAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/regenerate` as const;
}

export function dashboardUpdateAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/update` as const;
}

export function dashboardPromptAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/prompt` as const;
}

export function dashboardRefreshAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/refresh` as const;
}

export function dashboardSchedulesAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/schedules` as const;
}

export function dashboardScheduleAPI<C extends number | string, I extends string, S extends string>(companyId: C, dashboardId: I, scheduleId: S) {
  return `${dashboardSchedulesAPI(companyId, dashboardId)}/${scheduleId}` as const;
}

export function dashboardScheduleRecipientsAPI<C extends number | string, I extends string, S extends string>(companyId: C, dashboardId: I, scheduleId: S) {
  return `${dashboardScheduleAPI(companyId, dashboardId, scheduleId)}/recipients` as const;
}

export function dashboardScheduleSubscriptionAPI<C extends number | string, I extends string, S extends string>(companyId: C, dashboardId: I, scheduleId: S) {
  return `${dashboardScheduleAPI(companyId, dashboardId, scheduleId)}/subscription` as const;
}

export function dashboardMetadataAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/metadata` as const;
}

export function dashboardFavoriteAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/favorite` as const;
}

export function dashboardArchiveAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/archive` as const;
}

export function dashboardRestoreAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/restore` as const;
}

export function dashboardVersionsAPI<C extends number | string, I extends string>(companyId: C, dashboardId: I) {
  return `${dashboardAPI(companyId, dashboardId)}/versions` as const;
}

export function dashboardVersionRestoreAPI<C extends number | string, I extends string, V extends string>(
  companyId: C,
  dashboardId: I,
  versionId: V,
) {
  return `${dashboardVersionsAPI(companyId, dashboardId)}/${versionId}/restore` as const;
}

export function dashboardVersionActivateAPI<C extends number | string, I extends string, V extends string>(
  companyId: C,
  dashboardId: I,
  versionId: V,
) {
  return `${dashboardVersionsAPI(companyId, dashboardId)}/${versionId}/activate` as const;
}

// Org APIs

export function orgsAPI() {
  return "/api/v1/org" as const;
}

export function orgAPI<T extends string>(orgId: T) {
  return `${orgsAPI()}/${orgId}` as const;
}

export function orgCreditDeductionMultipliersAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/credit-deduction-multipliers` as const;
}

export function orgCompaniesAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/companies` as const;
}

export function orgExportAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/export` as const;
}

export function orgUsersAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/users` as const;
}

export function orgUsageAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/usage/fetch` as const;
}

export function orgDailyCreditUsageAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/daily-credit-usage` as const;
}

export function orgCreditPlanAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/credit-plan` as const;
}

export function orgCreditPacksAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/credit-packs` as const;
}

export function orgTrialAPI<T extends string>(orgId: T) {
  return `${orgAPI(orgId)}/billing/trial` as const;
}

// Terms of Service (TOS) APIs

const tosAPIBase = '/api/v1/tos';

export function tosAcceptAPI() {
  return `${tosAPIBase}/accept` as const;
}

export function tosStatusAPI() {
  return `${tosAPIBase}/status` as const;
}

export function mcpAuthorizeApproveAPI() {
  return `/api/v1/mcp/authorize/approve` as const;
}

export function notionAttachmentsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/notion-attachments` as const;
}

export function notionAttachmentAPI<C extends number | string>(companyId: C, attachmentId: string) {
  return `${companyAPI(companyId)}/notion-attachments/${attachmentId}` as const;
}

export function notionDatabaseChildrenAPI<C extends number | string>(companyId: C, dataSourceId: string) {
  return `${companyAPI(companyId)}/notion-database-children?dataSourceId=${encodeURIComponent(dataSourceId)}` as const;
}

export function notionPageChildrenAPI<C extends number | string>(companyId: C, pageId: string) {
  return `${companyAPI(companyId)}/notion-page-children?pageId=${encodeURIComponent(pageId)}` as const;
}

export function confluenceAttachmentsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/confluence-attachments` as const;
}

export function confluenceAttachmentAPI<C extends number | string>(companyId: C, attachmentId: string) {
  return `${companyAPI(companyId)}/confluence-attachments/${attachmentId}` as const;
}

export function confluenceSpaceChildrenAPI<C extends number | string>(companyId: C, spaceId: string) {
  return `${companyAPI(companyId)}/confluence-space-children?spaceId=${encodeURIComponent(spaceId)}` as const;
}

export function confluenceRootsAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/confluence-roots` as const;
}

export function reportingBaseAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/reporting` as const;
}

export function reportingSettingsAPI<C extends number | string>(companyId: C) {
  return `${reportingBaseAPI(companyId)}/settings` as const;
}

export function reportingUserPrefsAPI<C extends number | string>(companyId: C) {
  return `${reportingBaseAPI(companyId)}/preferences` as const;
}

export function defaultDailyReportInstructionAPI<C extends number | string>(companyId: C) {
  return `${reportingBaseAPI(companyId)}/default-instruction` as const;
}

export function defaultReportInstructionAPI<C extends number | string>(companyId: C) {
  return defaultDailyReportInstructionAPI(companyId);
}

export function homeReportBaseAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/digests` as const;
}

export function homeReportAPI<C extends number | string>(companyId: C, reportId: string) {
  return `${companyAPI(companyId)}/digest/${encodeURIComponent(reportId)}` as const;
}

export function homeReportLatestAPI<C extends number | string>(
  companyId: C, mode: "personal" | "workspace" | "team", teamId?: string,
) {
  const base = `${homeReportBaseAPI(companyId)}/latest?mode=${mode}` as const;
  return teamId ? `${base}&teamId=${encodeURIComponent(teamId)}` : base;
}

export function homeReportPreviewAPI<C extends number | string>(companyId: C) {
  return `${homeReportBaseAPI(companyId)}/preview` as const;
}

export function homeReportHistoryAPI<C extends number | string>(
  companyId: C, mode: "personal" | "workspace" | "team" | "all", teamId?: string,
) {
  const base = `${homeReportBaseAPI(companyId)}/history?mode=${mode}` as const;
  return teamId ? `${base}&teamId=${encodeURIComponent(teamId)}` : base;
}

export function homeReportCreateAPI<C extends number | string>(companyId: C) {
  return `${homeReportBaseAPI(companyId)}` as const;
}

export function workspaceGraphSubgraphAPI<C extends number | string>(companyId: C) {
  return `${companyAPI(companyId)}/workspace-graph/subgraph` as const;
}
